/**
 * 连线规则 —— 纯函数，不含任何 Vue / Vue Flow 依赖。
 *
 * 抽出来的目的有两个：
 *   1. 可以脱离 UI 直接测试（见 scripts/verify-connection-rules.ts）
 *   2. 以后接画布视图或换图引擎时，这套规则可以原样复用
 */

export interface EdgeLike {
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
  /**
   * 静音这条连线（Blender 的 Ctrl+Alt+RMB）。
   *
   * 语义上等同于「没有这条连线」：求值时被跳过，下游回退自己的内联默认值。
   * 但边本身保留，所以随时能取消静音 —— 比删掉再重连方便。
   */
  muted?: boolean
}

/*
 * `sameHandle` 的实现已下沉到 `node/handles.ts` —— 它被 `node/validate.ts`
 * 用着，而纯函数层不该反向 import 这一层。这里只是转出去，
 * 让既有引用（以及本文件的内部调用）不用改。
 */
export { sameHandle } from '../node/handles'
import { sameHandle } from '../node/handles'

/**
 * 判断加入 `sourceNodeId → targetNodeId` 这条边之后是否会形成环。
 *
 * 数据流方向即边的方向（output → input）。做法是把待加入的边
 * 先放进邻接表，然后从 target 出发沿数据流方向搜索，若能回到 source
 * 说明 source 的值最终依赖它自己 —— 成环。
 *
 * 注意检查的是**整张图**，不只是两条边互连：A→B→C→A 这种三节点环同样会被拦下。
 *
 * @param edges        当前已有的边
 * @param sourceNodeId 待连线的起点（输出侧节点）
 * @param targetNodeId 待连线的终点（输入侧节点）
 */
export function wouldCreateCycle(
  edges: readonly EdgeLike[],
  sourceNodeId: string,
  targetNodeId: string,
): boolean {
  // 自连必然成环
  if (sourceNodeId === targetNodeId) return true

  // 邻接表：source → target[]（数据流方向）
  const adjacency = new Map<string, string[]>()
  for (const edge of edges) {
    const list = adjacency.get(edge.source)
    if (list) list.push(edge.target)
    else adjacency.set(edge.source, [edge.target])
  }

  // 从 target 出发做深度优先搜索，看能否到达 source
  const visited = new Set<string>()
  const stack: string[] = [targetNodeId]

  while (stack.length > 0) {
    const current = stack.pop() as string
    if (current === sourceNodeId) return true
    if (visited.has(current)) continue
    visited.add(current)

    const next = adjacency.get(current)
    if (next) stack.push(...next)
  }

  return false
}

/**
 * 一个输入 socket 只接一条边 —— 返回摘掉指定输入上已有边之后的结果。
 *
 * 对齐契约：「每个 socket 汇聚一条入边」。
 * 语义是**覆盖**而不是拒绝：重连一次等于改接，不需要先手动断开
 * （Blender 等节点编辑器都是这个做法）。
 *
 * ⚠️ 为什么应用层必须自己做这件事：
 * Vue Flow 的 `connectionExists` 只拦「完全相同的端口对」，
 * 它拦不住「同一个输入换了另一个来源」——
 * 那样就会在同一个输入端口上堆出多条线。
 *
 * 输出端**不受限制**：一个输出扇出到多个输入是合法的、也是必须的。
 */
export function withoutTargetEdge<E extends EdgeLike>(
  edges: readonly E[],
  targetNodeId: string,
  targetHandleId?: string | null,
): E[] {
  return edges.filter(
    (edge) =>
      !(
        edge.target === targetNodeId &&
        sameHandle(edge.targetHandle, targetHandleId)
      ),
  )
}

/**
 * 找出「同一输入 socket 上挂了多条边」的分组。
 *
 * 正常情况下应该永远返回空数组。留作开发期护栏与测试断言 ——
 * 一旦触发，说明覆盖逻辑被改坏了，这比在界面上肉眼发现重影线早得多。
 */
export function findDuplicateInputs(edges: readonly EdgeLike[]): string[] {
  const seen = new Set<string>()
  const duplicates = new Set<string>()

  for (const edge of edges) {
    const key = `${edge.target}::${edge.targetHandle ?? ''}`
    if (seen.has(key)) duplicates.add(key)
    seen.add(key)
  }

  return [...duplicates]
}

/**
 * 这条边是否已经存在。
 *
 * 用于**连线前**就把「已经连过了」判为不可连 —— 而不是放行后静默无事发生。
 * 静默 no-op 是最糟的反馈：端口在拖拽时会高亮（暗示可以连），松手却毫无变化。
 */
export function hasEdge(
  edges: readonly EdgeLike[],
  connection: {
    source: string
    sourceHandle?: string | null
    target: string
    targetHandle?: string | null
  },
): boolean {
  return edges.some(
    (edge) =>
      edge.source === connection.source &&
      edge.target === connection.target &&
      sameHandle(edge.sourceHandle, connection.sourceHandle) &&
      sameHandle(edge.targetHandle, connection.targetHandle),
  )
}

/**
 * 找出挂在某个输入 socket 上的边。
 *
 * 正常至多一条（见 withoutTargetEdge）。用于在「改接」时告诉用户
 * 原来的来源是谁 —— 覆盖语义本身没问题，但不说明白就会让人以为是自己看错了。
 */
export function findEdgeOnInput<E extends EdgeLike>(
  edges: readonly E[],
  targetNodeId: string,
  targetHandleId?: string | null,
): E | undefined {
  return edges.find(
    (edge) =>
      edge.target === targetNodeId && sameHandle(edge.targetHandle, targetHandleId),
  )
}

/** handle 名的等价比较 —— 对外暴露，供调用方做同一套判定 */
